/*
  File: UserPage.tsx
  Author: Binish Mookken
  Date: April-June 2024

  Description:

*/

import React, { useEffect, useState } from "react";
import "./UsersPage.css";
import { motion } from "framer-motion";
import {
  Button,
  Empty,
  Flex,
  Input,
  message,
  Popconfirm,
  Space,
  Spin,
  Tag,
} from "antd";
import ManageUserModal from "./UserManageModal";
import {
  createUser,
  getPasscode,
  getUsers,
  patchUser,
} from "./service/service";
import Header from "./Header";
import usersIcon from "./icons/users.gif";
import { RolesProvider } from "./context/RolesContext";
import { FaAddressBook, FaCogs, FaMailBulk, FaPhoneAlt } from "react-icons/fa";
import { User } from "./types";
import UserNewModal from "./UserNewModal";
import ChagnePasswordModal from "./ChangePasswordModal";
import parsePhoneNumberFromString from "libphonenumber-js";

const UsersPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [users, setUsers] = useState<any[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [userIdToNameMap, setUserIdToNameMap] = useState<{
    [key: string]: string;
  }>({});
  const [manageUserModalVisible, setManageUserModalVisible] = useState(false);
  const [addUserModalVisible, setAddUserModalVisible] = useState(false);

  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [selectedLetters, setSelectedLetters] = useState<string[]>([]);
  const [resetModalVisible, setResetModalVisible] = useState(false);

  const [existingEmails, setExistingEmails] = useState<string[]>([]);
  const [existingPhoneNumbers, setExistingPhoneNumbers] = useState<string[]>(
    []
  );

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await getUsers();
        setUsers(response);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    // Generate the user ID to name mapping
    if (users) {
      const idToNameMap: { [key: string]: string } = {};
      users.forEach((user) => {
        idToNameMap[user.id] = `${user.first_name} ${user.last_name}`;
      });
      setUserIdToNameMap(idToNameMap);
    }

    const existingEmails: string[] = users.map((user) => user.email);
    const existingPhoneNumbers: string[] = users.map(
      (user) => user.phone_number
    );
    setExistingEmails(existingEmails);
    setExistingPhoneNumbers(existingPhoneNumbers);
  }, [users]);

  const getUserName = (userId: string) => {
    return userIdToNameMap[userId] || "Unknown";
  };

  const handleViewManageUserModal = (user: any) => {
    setSelectedUser(user);
    setManageUserModalVisible(true);
  };

  const roleColorsMap: {
    [key: string]: { backgroundColor: string; color: string; border: string };
  } = {
    "System Role": { color: "#f50", backgroundColor: "#000", border: "none" },
    "Agent Role": { color: "#2db7f5", backgroundColor: "#000", border: "none" },
  };

  const getRoleColors = (roleName: string) => {
    if (roleName in roleColorsMap) {
      return roleColorsMap[roleName];
    } else {
      return { backgroundColor: "#ccc", color: "#000", border: "none" };
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleLetterClick = (letter: string) => {
    if (selectedLetters.includes(letter)) {
      setSelectedLetters(selectedLetters.filter((l) => l !== letter));
    } else {
      setSelectedLetters([...selectedLetters, letter]);
    }
  };

  const countUsersByLetter = (letter: string) => {
    return users.filter((user) =>
      user.full_name.toLowerCase().startsWith(letter.toLowerCase())
    ).length;
  };

  const filterUsersByLetter = (user: any) => {
    if (selectedLetters.length === 0) return true;
    return selectedLetters.some((letter) =>
      user.full_name.toLowerCase().startsWith(letter.toLowerCase())
    );
  };

  const filteredUsers = users
    ? users.filter(
        (user) =>
          user.full_name.toLowerCase().includes(searchText.toLowerCase()) &&
          filterUsersByLetter(user)
      )
    : [];

  const [resetToken, setResetToken] = useState("");

  const [confirmResetTokenModalVisible, setConfirmResetTokenModalVisible] =
    useState(false); // State to control the visibility of the reset token confirmation modal

  const [selectedUserForResetToken, setSelectedUserForResetToken] = useState<
    any | null
  >(null); // State to store the selected user for reset token

  const [popConfirmVisible, setPopConfirmVisible] = useState<{
    [key: string]: boolean;
  }>({}); // State to manage Popconfirm visibility for each user

  const handleConfirmResetToken = (user: any) => {
    setSelectedUserForResetToken(user);
    setConfirmResetTokenModalVisible(true);
  };

  const formatPhoneNumber = (phoneNumber: string) => {
    if (phoneNumber != undefined) {
      const phoneNumberObj = parsePhoneNumberFromString(phoneNumber, "US");
      if (phoneNumberObj) {
        // Format the phone number as (XXX) XXX-XXXX
        return phoneNumberObj.formatNational();
      }
      return phoneNumber;
    }
    return "";
  };

  // Function to show Popconfirm for a specific user
  const showPopconfirmForUser = async (userId: string) => {
    setPopConfirmVisible({ ...popConfirmVisible, [userId]: true });
    try {
      const response = await getPasscode(userId);
      const response_passcode = response.data;
      setResetToken(response_passcode.passcode);
    } catch (error: any) {
      const error_message = error?.response?.data?.exception?.message;
      setResetToken(`Unable to fetch passcode ${error_message}`);
    }
  };

  // Function to handle confirmation in Popconfirm
  const handleConfirmPopconfirm = (userId: string) => {
    console.log("Confirm action for user:", userId);
    setPopConfirmVisible({ ...popConfirmVisible, [userId]: false });
  };

  const handleCancelPopconfirm = (userId: string) => {
    console.log("Cancel action for user:", userId);
    setPopConfirmVisible({ ...popConfirmVisible, [userId]: false });
  };

  const [passocdeModalPpen, setPassocdeModalOpen] = useState(false);
  const showModal = (user: any) => {
    setSelectedUserForResetToken(user);
    setPassocdeModalOpen(true);
  };

  const handleOk = (e: React.MouseEvent<HTMLElement>) => {
    setPassocdeModalOpen(false);
  };

  const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
    setPassocdeModalOpen(false);
  };

  const handleAddNewUser = async (user: any) => {
    try {
      const response = await createUser({
        email: user.email,
        password: "pass",
        phoneNumber: user.phone,
        firstName: user.firstName,
        lastName: user.lastName,
        roles: user.roles,
      });
      const createdNewUser = response;
      setUsers((prevUsers) => [...prevUsers, createdNewUser]);
      message.success("Successfully created new user");
      setAddUserModalVisible(false);
    } catch (error: any) {
      let error_message = "";
      if (error?.response?.data?.validations) {
        for (const validation of error?.response?.data?.validations) {
          error_message += `${validation.message} \n`;
        }
      }
      if (error?.response?.data?.exception) {
        error_message = error?.response?.data?.exception?.message;
      }
      message.error(<>{error_message}</>);
    }
  };

  const handleSaveManageUser = async (patchPayload: any) => {
    try {
      console.log(patchPayload);
      const updatedUser = await patchUser(selectedUser.id, patchPayload);
      setUsers((prevUsers) => {
        return prevUsers.map((user) =>
          user.id === updatedUser.id ? updatedUser : user
        );
      });

      message.success("Successfully updated user");
      setManageUserModalVisible(false);
    } catch (error: any) {
      let error_message = "";
      if (error?.response?.data?.validations) {
        for (const validation of error?.response?.data?.validations) {
          error_message += `${validation.message} \n`;
        }
      }
      if (error?.response?.data?.exception) {
        error_message = error?.response?.data?.exception?.message;
      }
      message.error(<>{error_message}</>);
    }
  };

  return (
    <RolesProvider>
      <div className="dashboard">
        <div className="dashboard-container">
          <Header
            header="Users"
            subheader="
            View and manage existing users, roles, and permission or add new users."
            icon={usersIcon}
          />

          <div style={{ background: "black", padding: "10px" }}>
            <div className="search-container">
              <Input
                placeholder="Search..."
                allowClear
                size="large"
                value={searchText}
                onChange={handleSearch}
                className="apple-search"
              />
            </div>

            <div className="letters-container">
              {Array.from(Array(26), (_, i) => String.fromCharCode(65 + i)).map(
                (letter) => (
                  <Button
                    key={letter}
                    type={
                      selectedLetters.includes(letter) ? "primary" : "default"
                    }
                    onClick={() => handleLetterClick(letter)}
                    disabled={countUsersByLetter(letter) === 0}
                    style={{
                      fontFamily: "Ubuntu",
                      borderRadius: "6px",
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>{letter}</span> &nbsp;
                    <span
                      style={{
                        color: "gray",
                        fontSize: "11px",
                        fontWeight: 700,
                      }}
                    >
                      {countUsersByLetter(letter)}
                    </span>
                  </Button>
                )
              )}
            </div>
          </div>

          <div style={{ background: "#000" }}>
            <div style={{ display: "flex", padding: "10px" }}>
              <div style={{ flex: 2 }}>
                <b>{filteredUsers.length}</b> users found
              </div>
              <div style={{ flex: 1 }}>
                <Flex gap="small" style={{ justifyContent: "flex-end" }}>
                  <Button
                    type="primary"
                    block
                    onClick={() => setAddUserModalVisible(true)}
                  >
                    <span style={{ fontWeight: 700 }}>New User</span>
                  </Button>
                </Flex>
              </div>
            </div>

            <div className="settings-content">
              {loading ? (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Spin tip="Loading users, please wait" size="large" />
                </div>
              ) : filteredUsers.length === 0 ? (
                <Empty description="No Users Found" />
              ) : (
                <div>
                  {filteredUsers.map((user) => (
                    <div
                      key={user.id}
                      style={{
                        background: "rgb(25, 25, 25)",
                        margin: "10px",
                        padding: "10px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "22px",
                            fontFamily: "ubuntu",
                            fontWeight: "700",
                          }}
                        >
                          {user.full_name}
                        </div>

                        <div style={{ textAlign: "right", marginTop: "8px" }}>
                          <Button.Group>
                            <Button
                              icon={<FaCogs />}
                              onClick={() => handleViewManageUserModal(user)}
                            >
                              Manage
                            </Button>
                          </Button.Group>
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "8px",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <Space>
                            <FaMailBulk color="#393939" />
                            <span>{user.email}</span>
                          </Space>
                        </div>
                        <div style={{ flex: 2 }}>
                          <Space>
                            <FaPhoneAlt color="#393939" />
                            <span>
                              {user ? formatPhoneNumber(user.phone_number) : ""}
                            </span>
                          </Space>
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: 2,
                          margin: "10px 0px",
                        }}
                      >
                        <div>
                          <Tag
                            color={user.status === "active" ? "green" : "red"}
                            className="agent-status"
                          >
                            {user.status.toUpperCase()}
                          </Tag>
                          {user.status.toUpperCase() == "FORGOT-PASSWORD" && (
                            <Popconfirm
                              title="Reset Token for password reset?"
                              description={<h4>{resetToken}</h4>}
                              open={popConfirmVisible[user.id]} // Set visibility based on state
                              onConfirm={() => handleConfirmPopconfirm(user.id)}
                              onCancel={() => handleCancelPopconfirm(user.id)}
                            >
                              {/* <Button type="primary" onClick={showModal(user)}>
                              Open Modal with customized button props
                            </Button> */}
                              <Button
                                type="link"
                                style={{ marginRight: "10px" }}
                                onClick={() => showPopconfirmForUser(user.id)} // Call showPopconfirmForUser function on click
                              >
                                Reset Token
                              </Button>
                            </Popconfirm>
                          )}
                        </div>
                        <Flex gap="4px 0" wrap="wrap">
                          {user.roles.map(
                            (role: { id: React.Key; name: string }) => {
                              const { backgroundColor, color, border } =
                                getRoleColors(role.name);
                              return (
                                <Tag
                                  key={role.id}
                                  className="role-tag"
                                  style={{
                                    backgroundColor,
                                    color,
                                    border,
                                    padding: "4px 8px",
                                    fontFamily: "ubuntu",
                                    fontSize: "15px",
                                    borderRadius: "0px",
                                  }}
                                >
                                  {role.name}
                                </Tag>
                              );
                            }
                          )}
                        </Flex>
                      </div>
                      <div className="agent-details">
                        <div className="agent-details-column">
                          <div className="agent-label">Created By</div>
                          <span> {getUserName(user.created_by)} </span>
                        </div>
                        <div className="agent-details-column">
                          <div className="agent-label">Created Time</div>
                          <span>
                            {new Date(user.created_ts).toLocaleDateString()}{" "}
                            {new Date(user.created_ts).toLocaleTimeString()}
                          </span>
                        </div>

                        <div className="agent-details-column">
                          <div className="agent-label">Updated Time</div>
                          <span>
                            {new Date(user.updated_ts).toLocaleDateString()}{" "}
                            {new Date(user.updated_ts).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="agent-details-column">
                          <div className="agent-label">Part of Network</div>
                          <span>{user?.agent != null ? "Yes" : "No"}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <ManageUserModal
              user={selectedUser}
              visible={manageUserModalVisible}
              onClose={() => setManageUserModalVisible(false)}
              onSaveUser={(user) => handleSaveManageUser(user)}
              existingEmails={existingEmails}
              existingPhones={existingPhoneNumbers}
            />
            <UserNewModal
              visible={addUserModalVisible}
              onClose={() => setAddUserModalVisible(false)}
              onAddUser={(user: User) => handleAddNewUser(user)}
              existingEmails={existingEmails}
              existingPhones={existingPhoneNumbers}
            />
            <ChagnePasswordModal
              visible={resetModalVisible}
              onClose={() => setResetModalVisible(false)}
              resetWithOutCurrentPassword={true}
            />
          </div>
        </div>
      </div>
    </RolesProvider>
  );
};

export default UsersPage;
